import { Home, Wrench, Zap, CheckCircle, Phone, Star, Shield, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '@/components/ui-custom/SEO';

const Residential = () => {
  const services = [
    {
      title: 'Kitchen Plumbing',
      description: 'Faucet repair, garbage disposal installation, and pipe work for your kitchen.',
      icon: Wrench,
    },
    {
      title: 'Bathroom Plumbing',
      description: 'Toilet repair, shower installation, and bathroom fixture upgrades.',
      icon: Wrench,
    },
    {
      title: 'Electrical Panels',
      description: 'Panel upgrades and electrical service improvements for your home.',
      icon: Zap,
    },
    {
      title: 'Lighting & Fans',
      description: 'Interior and exterior lighting, ceiling fans, and fixture installation.',
      icon: Zap,
    },
    {
      title: 'Water Heaters',
      description: 'Traditional and tankless water heater installation and repair.',
      icon: Wrench,
    },
    {
      title: 'Home Safety',
      description: 'GFCI outlets, smoke detectors, and whole-home safety inspections.',
      icon: Shield,
    },
  ];

  const whyChooseUs = [
    'Respectful, clean service in your home',
    'Upfront pricing before any work begins',
    'Same-day appointments available',
    'Licensed and insured technicians',
    '30-day workmanship guarantee',
    'Family-owned local business',
  ];

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'serviceType': 'Residential Plumbing and Electrical',
    'name': 'Residential Plumbing & Electrical Services in Dothan, AL',
    'description': 'Home plumbing and electrical services including kitchen and bathroom plumbing, water heaters, panel upgrades, lighting, and home safety throughout the Wiregrass area.',
    'provider': { '@type': 'LocalBusiness', 'name': "Ard's Plumbing & Electric", 'telephone': '+1-334-692-5537', 'url': 'https://ardsdothan.com' },
    'areaServed': { '@type': 'AdministrativeArea', 'name': 'Wiregrass Area, Alabama' },
    'url': 'https://ardsdothan.com/services/residential',
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': 'Residential Services',
      'itemListElement': [
        { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Kitchen Plumbing' } },
        { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Bathroom Plumbing' } },
        { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Electrical Panels' } },
        { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Lighting & Fans' } },
        { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Water Heaters' } },
        { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Home Safety' } },
      ],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': [
      { '@type': 'Question', 'name': 'Do you offer same-day residential service?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes, same-day appointments are available Mon-Fri 8AM-5PM. Call (334) 692-5537 to schedule.' } },
      { '@type': 'Question', 'name': 'Do you guarantee your residential work?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes, all our residential work comes with a 30-day workmanship guarantee.' } },
      { '@type': 'Question', 'name': 'Are your technicians licensed and insured?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes, all technicians are fully licensed and insured in the state of Alabama.' } },
      { '@type': 'Question', 'name': 'Do you serve all of the Wiregrass area?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes, we serve Dothan and surrounding communities including Enterprise, Ozark, Headland, Geneva, and more throughout the Wiregrass.' } },
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://ardsdothan.com/' },
      { '@type': 'ListItem', 'position': 2, 'name': 'Services', 'item': 'https://ardsdothan.com/services' },
      { '@type': 'ListItem', 'position': 3, 'name': 'Residential', 'item': 'https://ardsdothan.com/services/residential' },
    ],
  };

  const faqs = [
    { q: 'Do you offer same-day residential service?', a: 'Yes, same-day appointments are available Mon-Fri 8AM-5PM. Call (334) 692-5537 to schedule.' },
    { q: 'Do you guarantee your residential work?', a: 'Yes, all our residential work comes with a 30-day workmanship guarantee.' },
    { q: 'Are your technicians licensed and insured?', a: 'Yes, all technicians are fully licensed and insured in the state of Alabama.' },
    { q: 'Do you serve all of the Wiregrass area?', a: 'Yes, we serve Dothan and surrounding communities including Enterprise, Ozark, Headland, Geneva, and more throughout the Wiregrass.' },
  ];

  return (
    <>
      <SEO
        title="Residential Plumbing & Electrical Dothan AL | Ard's"
        description="Home plumbing and electrical services in Dothan, AL. Kitchens, baths, water heaters, panels, lighting & more. Same-day service. Call (334) 692-5537."
        keywords="residential plumber Dothan, home electrician, kitchen plumbing, bathroom plumbing, water heater installation, home electrical repair"
        canonical="/services/residential"
        additionalSchema={[serviceSchema, faqSchema, breadcrumbSchema]}
      />

      {/* Hero Section */}
      <section className="bg-brand-blue pt-32 pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 text-white text-sm px-4 py-2 rounded-full mb-6">
                <Home className="w-4 h-4" />
                Residential Services
              </div>
              <h1 className="font-heading font-black text-4xl lg:text-6xl text-white leading-tight mb-6">
                HOME SERVICES<br />
                <span className="text-brand-yellow">YOU CAN TRUST</span>
              </h1>
              <p className="text-lg text-white/90 mb-8 max-w-lg">
                Keep your home safe, efficient, and comfortable with our comprehensive 
                residential plumbing and electrical services.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  className="bg-brand-coral hover:bg-brand-coral/90 text-white rounded-full px-8 py-6 text-lg font-semibold"
                >
                  <Link to="/contact-us">Schedule Service</Link>
                </Button>
                <a
                  href="tel:3346925537"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full border-2 border-white text-white font-semibold hover:bg-white hover:text-brand-blue transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  (334) 692-5537
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src="/images/residential-service.jpg"
                alt="Residential plumbing and electrical services in Dothan AL"
                className="rounded-[28px] shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-brand-blue rounded-full flex items-center justify-center">
                    <Star className="w-6 h-6 text-white fill-white" />
                  </div>
                  <div>
                    <p className="font-bold text-brand-black">4+ Rating</p>
                    <p className="text-sm text-gray-500">Trusted by Homeowners</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading font-black text-3xl lg:text-5xl text-brand-black mb-4">
              RESIDENTIAL SERVICES
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Complete home plumbing and electrical solutions for homeowners in Dothan and the Wiregrass area.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-2xl p-6 hover:shadow-xl transition-shadow"
              >
                <div className="w-14 h-14 bg-brand-blue/10 rounded-xl flex items-center justify-center mb-4">
                  <service.icon className="w-7 h-7 text-brand-blue" />
                </div>
                <h3 className="font-heading font-bold text-xl text-brand-black mb-2">
                  {service.title}
                </h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 bg-brand-navy">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading font-black text-3xl lg:text-5xl text-white mb-6">
                YOUR HOME IS<br />
                <span className="text-brand-yellow">OUR PRIORITY</span>
              </h2>
              <p className="text-lg text-white/80 mb-8">
                We treat your home with the same care and respect we'd give our own. 
                Our technicians are clean, courteous, and committed to your satisfaction.
              </p>
              <ul className="space-y-4">
                {whyChooseUs.map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-white">
                    <CheckCircle className="w-5 h-5 text-brand-yellow flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white/10 rounded-2xl p-6 text-center">
                <Home className="w-10 h-10 text-brand-yellow mx-auto mb-3" />
                <p className="font-bold text-white text-lg">Home</p>
                <p className="text-white/70 text-sm">Focused</p>
              </div>
              <div className="bg-white/10 rounded-2xl p-6 text-center">
                <Clock className="w-10 h-10 text-brand-yellow mx-auto mb-3" />
                <p className="font-bold text-white text-lg">Same-Day</p>
                <p className="text-white/70 text-sm">Service</p>
              </div>
              <div className="bg-white/10 rounded-2xl p-6 text-center">
                <Shield className="w-10 h-10 text-brand-yellow mx-auto mb-3" />
                <p className="font-bold text-white text-lg">Licensed</p>
                <p className="text-white/70 text-sm">& Insured</p>
              </div>
              <div className="bg-white/10 rounded-2xl p-6 text-center">
                <Star className="w-10 h-10 text-brand-yellow mx-auto mb-3" />
                <p className="font-bold text-white text-lg">5-Star</p>
                <p className="text-white/70 text-sm">Rated</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading font-black text-3xl lg:text-5xl text-brand-black mb-4">
              FREQUENTLY ASKED QUESTIONS
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-heading font-bold text-lg text-brand-black mb-2">{faq.q}</h3>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-brand-coral">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-black text-3xl lg:text-5xl text-white mb-6">
            READY TO GET STARTED?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Contact Ard's Plumbing & Electric today for reliable home services 
            in Dothan and the Wiregrass area.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              className="bg-white text-brand-coral hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-semibold"
            >
              <Link to="/contact-us">Book Online</Link>
            </Button>
            <a
              href="tel:3346925537"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full border-2 border-white text-white font-semibold hover:bg-white hover:text-brand-coral transition-colors"
            >
              <Phone className="w-5 h-5" />
              Call (334) 692-5537
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Residential;
